import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["images", "mainImage", "thumbnail", "thumbnailList", "zoom"]

  connect() {
    this.refreshAfterImageLoad = () => this.refreshZoom()
    this.mainImageTarget.addEventListener("load", this.refreshAfterImageLoad)

    if (this.hasThumbnailTarget) {
      const selectedThumbnail = this.thumbnailTargets.find(thumbnail => thumbnail.classList.contains("selected"))
      this.selectThumbnail(selectedThumbnail || this.thumbnailTarget, false)
    } else if (this.mainImageTarget.complete) {
      this.refreshZoom()
    }

    this.element.dataset.productGalleryReady = "true"
  }

  disconnect() {
    this.mainImageTarget.removeEventListener("load", this.refreshAfterImageLoad)
    cancelAnimationFrame(this.resizeFrame)
    this.destroyZoom()
    delete this.element.dataset.productGalleryReady
  }

  select(event) {
    this.selectThumbnail(event.currentTarget)
  }

  resize() {
    cancelAnimationFrame(this.resizeFrame)
    this.resizeFrame = requestAnimationFrame(() => this.refreshZoom())
  }

  refreshZoom() {
    this.destroyZoom()

    if (!this.mainImageTarget.complete || !this.mainImageTarget.classList.contains("zoomable")) return
    if (this.mainImageTarget.offsetParent === null || !this.hasZoomTarget) return

    const thumbnailListMargin = this.hasThumbnailListTarget
      ? parseFloat(getComputedStyle(this.thumbnailListTarget).marginBottom) || 0
      : 0
    const imagesMargin = parseFloat(getComputedStyle(this.imagesTarget).marginTop) || 0
    const zoomStyle = getComputedStyle(this.zoomTarget)
    const horizontalPadding = (parseFloat(zoomStyle.paddingLeft) || 0) + (parseFloat(zoomStyle.paddingRight) || 0)
    const imageRect = this.mainImageTarget.getBoundingClientRect()
    const availableZoomWidth = this.zoomTarget.clientWidth - horizontalPadding
    const availableZoomHeight = this.imagesTarget.offsetHeight - thumbnailListMargin + imagesMargin
    const zoomWidth = Math.min(availableZoomWidth, this.mainImageTarget.naturalWidth)
    const zoomHeight = Math.min(availableZoomHeight, this.mainImageTarget.naturalHeight)
    const scaleX = this.mainImageTarget.naturalWidth / imageRect.width
    const scaleY = this.mainImageTarget.naturalHeight / imageRect.height
    if (zoomWidth <= 0 || zoomHeight <= 0 || scaleX <= 1 || scaleY <= 1) return

    this.zoomWindow = document.createElement("div")
    this.zoomWindow.className = "product-zoom-window"
    this.zoomWindow.hidden = true
    this.zoomWindow.setAttribute("aria-hidden", "true")
    this.zoomWindow.style.width = `${zoomWidth}px`
    this.zoomWindow.style.height = `${zoomHeight}px`
    this.zoomWindow.style.backgroundImage = `url(${JSON.stringify(this.mainImageTarget.currentSrc || this.mainImageTarget.src)})`
    this.zoomWindow.style.backgroundSize = `${this.mainImageTarget.naturalWidth}px ${this.mainImageTarget.naturalHeight}px`
    this.zoomTarget.append(this.zoomWindow)

    this.zoomLens = document.createElement("div")
    this.zoomLens.className = "product-zoom-lens"
    this.zoomLens.hidden = true
    this.zoomLens.style.width = `${Math.min(imageRect.width, zoomWidth / scaleX)}px`
    this.zoomLens.style.height = `${Math.min(imageRect.height, zoomHeight / scaleY)}px`
    this.mainImageTarget.parentElement.append(this.zoomLens)

    this.zoomState = { scaleX, scaleY }
    this.element.dataset.productZoomReady = "true"
  }

  showZoom(event) {
    if (!this.zoomState) return

    this.zoomWindow.hidden = false
    this.zoomLens.hidden = false
    this.element.dataset.productZoomActive = "true"
    this.moveZoom(event)
  }

  moveZoom(event) {
    if (!this.zoomState || this.zoomWindow.hidden) return

    const imageRect = this.mainImageTarget.getBoundingClientRect()
    const lensWidth = this.zoomLens.offsetWidth
    const lensHeight = this.zoomLens.offsetHeight
    const left = this.clamp(event.clientX - imageRect.left - lensWidth / 2, 0, imageRect.width - lensWidth)
    const top = this.clamp(event.clientY - imageRect.top - lensHeight / 2, 0, imageRect.height - lensHeight)

    this.zoomLens.style.left = `${this.mainImageTarget.offsetLeft + left}px`
    this.zoomLens.style.top = `${this.mainImageTarget.offsetTop + top}px`
    this.zoomWindow.style.backgroundPosition = `${-left * this.zoomState.scaleX}px ${-top * this.zoomState.scaleY}px`
  }

  hideZoom() {
    if (!this.zoomState) return

    this.zoomWindow.hidden = true
    this.zoomLens.hidden = true
    delete this.element.dataset.productZoomActive
  }

  selectThumbnail(thumbnail, updateImage = true) {
    this.thumbnailTargets.forEach(candidate => {
      const selected = candidate === thumbnail
      candidate.classList.toggle("selected", selected)
      candidate.setAttribute("aria-pressed", selected.toString())
    })

    this.destroyZoom()
    this.mainImageTarget.classList.toggle("zoomable", thumbnail.dataset.zoomable === "true")

    if (updateImage && this.mainImageTarget.getAttribute("src") !== thumbnail.dataset.imageUrl) {
      this.mainImageTarget.setAttribute("src", thumbnail.dataset.imageUrl)
    }

    if (this.mainImageTarget.complete) this.refreshZoom()
  }

  destroyZoom() {
    this.zoomWindow?.remove()
    this.zoomLens?.remove()
    this.zoomWindow = null
    this.zoomLens = null
    this.zoomState = null
    delete this.element.dataset.productZoomReady
    delete this.element.dataset.productZoomActive
  }

  clamp(value, minimum, maximum) {
    return Math.min(Math.max(value, minimum), maximum)
  }
}
